import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import type { SearchableDropdownOption } from 'components/ds/SearchableDropdown'
import { SearchableDropdown } from 'components/ds/SearchableDropdown'
import { CreateNewAction } from 'components/ds/SearchableDropdown/CreateNewAction'
import { OptionWithEditIcon } from 'components/ds/SearchableDropdown/OptionWithEditIcon'

import { RIGHTS_DROPDOWN_WIDTH } from '../../constants'
import { formatIdentifiersForDisplay } from '../../utils/identifier-validation'
import { checkIfLoading, getOptions } from '../dropdown-utils'

export interface Pro {
  id: number
  name: string
  external_identifiers?: ExternalIdentifier[]
}

export interface ProDropdownProps {
  /** Current selected PRO ID */
  value?: string
  /** Callback when a PRO is selected */
  onSelect: (option: SearchableDropdownOption<Pro>) => void
  /** Preloaded PROs from Redux store */
  pros?: Pro[]
  /** PRO IDs to prioritize at the top of the list (e.g., writer's associated PROs) */
  priorityIds?: number[]
  /** Placeholder text for the search input */
  placeholder?: string
  /** Whether the dropdown is disabled */
  disabled?: boolean
  /** Optional ARIA label for accessibility */
  'aria-label'?: string
  /** Split ID to identify which split row triggered the creation */
  splitId?: string | number
  /** Splits key (track ID or BULK_EDIT_KEY) for updating the correct split after creation */
  splitsKey?: number | string
}

/**
 * ProDropdown - A specialized dropdown for selecting PROs (Performance Rights Organizations)
 *
 * This component uses the SearchableDropdown component to provide a searchable
 * interface for selecting PROs. It uses preloaded PRO data from Redux store and
 * displays each option with an edit icon.
 *
 * PROs are organizations like BMI, ASCAP, STIM, etc. that collect royalties
 * on behalf of songwriters and publishers.
 *
 * Features:
 * - Searchable PRO selection
 * - Edit icon for each PRO (links to edit route when implemented)
 * - "Create new PRO" action button
 *
 * @param props - Component props
 * @returns ProDropdown component
 *
 * @example
 * ```tsx
 * <ProDropdown
 *   value={selectedProId}
 *   onSelect={(option) => handleProSelect(option)}
 *   pros={prosFromRedux}
 *   placeholder="Select PRO..."
 * />
 * ```
 */
export function ProDropdown({
  value,
  onSelect,
  pros = [],
  priorityIds = [],
  placeholder = 'PRO',
  disabled = false,
  'aria-label': ariaLabel = 'PRO dropdown',
  splitId,
  splitsKey,
}: ProDropdownProps) {
  const navigate = useNavigate()

  const options = getOptions(priorityIds, pros)
  const isLoading = checkIfLoading(value, options)

  // Custom option renderer with edit icon
  const renderOption = useCallback(
    (option: SearchableDropdownOption<Pro>) => {
      const secondaryText = formatIdentifiersForDisplay(
        option.data?.external_identifiers,
      )
      return (
        <OptionWithEditIcon
          label={option.label}
          secondaryText={secondaryText}
          onEditClick={() => {
            // Navigate to edit PRO modal
            // Include splitId and splitsKey for auto-selection after edit
            navigate(`rights-data/pros/${option.id}/edit`, {
              state: { splitId, splitsKey },
            })
          }}
        />
      )
    },
    [navigate, splitId, splitsKey],
  )

  // "Create new PRO" action component with relative path
  // Use function form to receive search value from SearchableDropdown
  const actionComponent = useCallback(
    (searchValue: string) => (
      <CreateNewAction
        label="Create new PRO"
        to="rights-data/pros/new"
        searchText={searchValue}
        splitId={splitId}
        splitsKey={splitsKey}
      />
    ),
    [splitId, splitsKey],
  )

  return (
    <SearchableDropdown
      value={value}
      onSelect={onSelect}
      options={options}
      loading={isLoading}
      placeholder={placeholder}
      renderOption={renderOption}
      actionComponent={actionComponent}
      aria-label={ariaLabel}
      disabled={disabled}
      maxHeight={208}
      customDropdownWidth={RIGHTS_DROPDOWN_WIDTH}
    />
  )
}

export default ProDropdown
